import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Bed, Bath, Maximize, MapPin } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getMainImageUrl = (images) => {
  if (!images || images.length === 0) return null;
  
  if (typeof images[0] === 'object') {
    const portada = images.find(img => img.category === 'portada');
    const url = portada?.url || images[0]?.url;
    if (url) {
      return url.startsWith('/') ? `${BACKEND_URL}${url}` : url;
    }
  }
  if (typeof images[0] === 'string') {
    const url = images[0];
    return url.startsWith('/') ? `${BACKEND_URL}${url}` : url;
  }
  return null;
};

export const PropertyListView = ({ properties, favorites, toggleFavorite, showOnlyFavorites }) => {
  const navigate = useNavigate();
  const { themes, theme: currentTheme } = useTheme();
  const themeData = themes[currentTheme] || themes.electric;

  if (!properties || properties.length === 0) {
    return (
      <div className="text-center py-20 px-6">
        {showOnlyFavorites ? (
          <>
            <div className="text-6xl mb-4">💔</div>
            <p className="text-xl text-gray-300 font-bold mb-2">Aún no tienes favoritos</p>
            <p className="text-sm text-gray-500">Pulsa el corazón en cualquier propiedad para guardarla aquí.</p>
          </>
        ) : (
          <p className="text-xl text-gray-400">No se encontraron propiedades</p>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
      {properties.map((property) => {
        const imageUrl = getMainImageUrl(property.images);
        const isFavorite = favorites.includes(property.property_id);

        return (
          <div 
            key={property.property_id}
            className="bg-gray-800/50 rounded-2xl overflow-hidden border border-white/10 hover:border-white/30 transition-all hover:scale-[1.02] cursor-pointer"
            onClick={() => navigate(`/property/${property.property_id}`)}
          >
            {/* Imagen */}
            <div className="relative h-48 bg-gray-900">
              {imageUrl ? (
                <img 
                  src={imageUrl} 
                  alt={property.title}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=Sin+Imagen'; }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-500">
                  Sin imagen
                </div>
              )}
              
              {/* Badge tipo */}
              <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold ${
                property.listing_type === 'alquiler' ? 'bg-red-500' : 'bg-green-500'
              } text-white`}>
                {property.listing_type === 'alquiler' ? 'Alquiler' : 'Venta'}
              </div>

              {/* Botón favorito */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFavorite(property.property_id);
                }}
                className="absolute top-3 right-3 p-2 bg-white/90 hover:bg-white rounded-full transition-all"
              >
                <Heart 
                  size={18} 
                  fill={isFavorite ? '#ef4444' : 'none'}
                  stroke={isFavorite ? '#ef4444' : '#374151'}
                  strokeWidth={2}
                />
              </button>
            </div>

            {/* Info */}
            <div className="p-4">
              {/* Precio */}
              <div className={`text-2xl font-black mb-2 bg-gradient-to-r ${themeData.gradient} bg-clip-text text-transparent`}>
                €{property.price.toLocaleString('es-ES')}
                {property.listing_type === 'alquiler' && <span className="text-base">/mes</span>}
              </div>

              {/* Título */}
              <h3 className="text-white font-bold text-lg mb-2 line-clamp-2">{property.title}</h3>

              {/* Ubicación */}
              <div className="flex items-center gap-1 text-gray-400 text-sm mb-3">
                <MapPin size={14} />
                <span className="line-clamp-1">{property.location}</span>
              </div>

              {/* Características */}
              <div className="flex items-center gap-4 text-gray-300 text-sm">
                {property.bedrooms && (
                  <div className="flex items-center gap-1">
                    <Bed size={16} />
                    <span>{property.bedrooms}</span>
                  </div>
                )}
                {property.bathrooms && (
                  <div className="flex items-center gap-1">
                    <Bath size={16} />
                    <span>{property.bathrooms}</span>
                  </div>
                )}
                {property.area && (
                  <div className="flex items-center gap-1">
                    <Maximize size={16} />
                    <span>{property.area}m²</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
